import React, { useReducer, useState } from 'react'
import { reviewRepliesPath, reviewReplyPath } from 'components/routes/api'
import { addCsrfHeader, api } from 'components/api'
import { DisplayReply, Editing, ReadyToEdit, ReadyToReply, Replying } from './ReplyForms'
import PropTypes from 'prop-types'
import { Reply } from './ReviewReply'
import { ReviewReplyAlertModal, ReviewReplyConfirmModal } from './ReviewReplyModal'

/**
 * States that correspond to the form to be rendered.
 * see also the state reducing function
 */
export enum ReplyFormState {
  READY_TO_EDIT = 'READY_TO_EDIT',
  EDITING = 'EDITING',
  READY_TO_REPLY = 'READY_TO_REPLY',
  REPLYING = 'REPLYING',
  STATIC = 'STATIC',
}

/**
 *  Actions that are able to affect state
 *  see also the state reducing function
 */
export enum ReplyActionType {
  CREATE_NEW = 'CREATE_NEW',
  CREATE_CANCEL = 'CREATE_CANCEL',
  CREATE_POST = 'CREATE_POST',
  EDIT_EXISTING = 'EDIT_EXISTING',
  EDIT_CANCEL = 'EDIT_CANCEL',
  EDIT_DELETE = 'EDIT_DELETE',
  EDIT_POST = 'EDIT_POST'
}

interface ReplyFormSelectorProps {
  reply?: Reply
  reviewId: number
  initialForm: ReplyFormState
}

interface ReplyAction {
  type: ReplyActionType
  reviewId: number
  replyId?: number
  replyText?: string
  updatedAt?: Date
}

interface ReplyState {
  replyId?: number
  reviewId: number
  replyText: string
  updatedAt?: Date

  form: ReplyFormState
}

/**
 * The state machine for selecting which form to use
 *
 * Form States:
 * READY_TO_REPLY: User is able to reply but there is no current reply. Shows 'Reply' button without any reply.
 * REPLYING: User is actively creating a reply.
 * READY_TO_EDIT: User is able to reply and there is an existing reply. Shows 'Edit' button with the current reply.
 * EDITING: User is actively editing an existing reply
 * STATIC: Render not-editable view of reply
 *
 * State Transitions and associated action
 * [ start state -> action -> end state ]
 * READY_TO_REPLY -> CREATE_NEW -> REPLYING
 * REPLYING -> CREATE_CANCEL -> READY_TO_REPLY
 * REPLYING -> CREATE_POST -> READY_TO_EDIT  (action includes updating the server)
 * READY_TO_EDIT -> EDIT_EXISTING -> EDITING
 * EDITING -> EDIT_CANCEL -> READY_TO_EDIT
 * EDITING -> EDIT_DELETE -> READY_TO_REPLY (action includes updating the server)
 * EDITING -> EDIT_POST -> READY_TO_EDIT (action includes updating the server)
 * STATIC no transitions
 *
 * Notes on updating state
 * If the state should be updated to a new dynamic value then:
 * newState = { stateItem: action.stateItem, ... }
 *
 * If the state should be updated to a static value then, irregardless of action or previous state:
 * newState = { stateItem: STATIC_VALUE, ... }
 *
 * If the state should remain unchanged then
 * newState = { stateItem: state.stateItem, ... }
 *
 *
 * @param state
 * @param action
 */
function replyStateReducer (state: ReplyState, action: ReplyAction): ReplyState {
  let newState: ReplyState
  switch (action.type) {
    case ReplyActionType.CREATE_NEW:
      newState = {
        reviewId: action.reviewId,
        replyText: '',
        updatedAt: undefined,
        form: ReplyFormState.REPLYING
      }
      break
    case ReplyActionType.CREATE_CANCEL:
      newState = {
        reviewId: action.reviewId,
        replyText: '',
        updatedAt: undefined,
        form: ReplyFormState.READY_TO_REPLY
      }
      break
    case ReplyActionType.CREATE_POST:
      newState = {
        reviewId: action.reviewId,
        replyId: action.replyId,
        replyText: action.replyText ?? '',
        updatedAt: action.updatedAt,
        form: ReplyFormState.READY_TO_EDIT
      }
      break
    case ReplyActionType.EDIT_EXISTING:
      newState = {
        reviewId: action.reviewId,
        replyId: action.replyId,
        replyText: action.replyText ?? '',
        updatedAt: action.updatedAt,
        form: ReplyFormState.EDITING
      }
      break
    case ReplyActionType.EDIT_DELETE:
      newState = {
        reviewId: action.reviewId,
        replyId: action.replyId,
        replyText: '',
        updatedAt: action.updatedAt,
        form: ReplyFormState.READY_TO_REPLY
      }
      break
    case ReplyActionType.EDIT_CANCEL:
      newState = {
        reviewId: action.reviewId,
        replyId: action.replyId,
        // For replyText and upatedAtuse current `state` rather than update through `action`
        replyText: state.replyText,
        updatedAt: state.updatedAt,
        form: ReplyFormState.READY_TO_EDIT
      }
      break
    case ReplyActionType.EDIT_POST:
      newState = {
        reviewId: action.reviewId,
        replyId: action.replyId,
        replyText: action.replyText ?? '',
        updatedAt: action.updatedAt,
        form: ReplyFormState.READY_TO_EDIT
      }
      break

    default:
      throw Error('Unknown action while reducing state')
  }
  return newState
}

const hasReply = (reply: Reply): boolean => (reply != null)

export function ReplyFormSelector (
  {
    reply,
    reviewId,
    initialForm
  }: ReplyFormSelectorProps): React.ReactElement {
  // Props can only be used to set the initial state, and should not be used anywhere else.
  const initialReducerState: ReplyState = {
    reviewId,
    replyId: reply?.id,
    replyText: reply?.text ?? '',
    updatedAt: reply?.updatedAt,
    form: initialForm
  }
  // After this point, the state object should be the source of all reply data
  const [state, dispatch] = useReducer(replyStateReducer, initialReducerState)
  // modal States
  const [showModalIfReplyEmpty, setShowModalIfReplyEmpty] = useState<boolean>(false)
  const [showConfirmModalOnDelete, setShowConfirmModalOnDelete] = useState<boolean>(false)

  const confirmedDelete = (shouldDelete: boolean): void => {
    if (!shouldDelete) { return }
    if (state.replyId == null) { return }
    const request = {
      url: reviewReplyPath(state.replyId),
      headers: addCsrfHeader({}, document)
    }
    api.delete(request)
      .then(
        resp => {
          if (resp.status === 200) {
            dispatch({
              type: ReplyActionType.EDIT_DELETE,
              reviewId: state.reviewId,
              replyId: undefined,
              replyText: '',
              updatedAt: undefined
            })
          }
        })
      .catch(console.error)
  }
  const handleDelete = (): void => {
    if (state.replyId == null) {
      alert('Invalid reply id. Nothing to delete')
      return
    }

    setShowConfirmModalOnDelete(true)
  }

  const handleCreateReply = (replyText: string): void => {
    if (replyText.trim().length < 1) {
      setShowModalIfReplyEmpty(true)
      return
    }

    const request = {
      url: reviewRepliesPath(reviewId),
      data: { text: replyText },
      headers: addCsrfHeader({}, document)
    }

    api.post<unknown, { review_reply: Reply }>(request).then(resp => {
      if (resp.status === 200) {
        const { review_reply: reply } = resp.data
        const updatedAtMaybe = hasReply(reply) ? reply.updatedAt : undefined
        const updatedAt = (updatedAtMaybe != null) ? new Date(updatedAtMaybe) : undefined
        dispatch({
          type: ReplyActionType.CREATE_POST,
          reviewId: state.reviewId,
          replyId: reply?.id,
          replyText: reply?.text,
          updatedAt
        })
      }
    }).catch(console.error)
  }

  const handleEditReply = (replyId: number | undefined) => (replyText: string): void => {
    if (replyId == null) {
      throw new Error('Cannot update reply without the replyId')
    }

    if (replyText.trim().length < 1) {
      setShowModalIfReplyEmpty(true)
      return
    }

    const request = {
      url: reviewReplyPath(replyId),
      data: { text: replyText },
      headers: addCsrfHeader({}, document)
    }

    api.put<unknown, { review_reply: Reply }>(request)
      .then(resp => {
        if (resp.status === 200) {
          const { review_reply: reply } = resp.data
          const updatedAtMaybe = hasReply(reply) ? reply.updatedAt : undefined
          const updatedAt = (updatedAtMaybe != null) ? new Date(updatedAtMaybe) : undefined
          dispatch({
            type: ReplyActionType.EDIT_POST,
            reviewId: state.reviewId,
            replyId: reply?.id,
            replyText: reply?.text,
            updatedAt
          })
        }
      })
      .catch((e) => {
        console.error(e)
        dispatch({
          type: ReplyActionType.CREATE_CANCEL,
          reviewId: state.reviewId,
          replyId: state.replyId,
          replyText: state.replyText,
          updatedAt: state.updatedAt
        })
      })
  }

  const replyForm = (): React.ReactElement => {
    switch (state.form) {
      case ReplyFormState.READY_TO_EDIT:
        return (
          <ReadyToEdit
            initialText={state.replyText}
            updatedAt={state.updatedAt}
            onClick={() => dispatch({
              type: ReplyActionType.EDIT_EXISTING,
              reviewId: state.reviewId,
              replyId: state.replyId,
              replyText: state.replyText,
              updatedAt: state.updatedAt
            })}
          />
        )
      case ReplyFormState.EDITING:
        return (
          <Editing
            initialText={state.replyText}
            reviewId={reviewId}
            onCancel={() => dispatch({
              type: ReplyActionType.EDIT_CANCEL,
              reviewId: state.reviewId,
              replyId: state.replyId,
              replyText: state.replyText,
              updatedAt: state.updatedAt
            })}
            onDelete={handleDelete}
            onPost={handleEditReply(state.replyId)}
          />
        )
      case ReplyFormState.READY_TO_REPLY:
        return (
          <ReadyToReply
            onClick={() => dispatch({
              type: ReplyActionType.CREATE_NEW,
              reviewId: state.reviewId,
              replyId: undefined,
              replyText: '',
              updatedAt: undefined
            })}
          />
        )
      case ReplyFormState.REPLYING:
        return (
          <Replying
            onCancel={() => dispatch({
              type: ReplyActionType.CREATE_CANCEL,
              reviewId: state.reviewId,
              replyId: undefined,
              replyText: '',
              updatedAt: undefined
            })}
            onPost={handleCreateReply}
          />
        )
      default:
        return <DisplayReply replyText={state.replyText} updatedAt={state.updatedAt} />
    }
  }

  return (
    <>
      {replyForm()}
      <ReviewReplyAlertModal open={showModalIfReplyEmpty} setOpen={setShowModalIfReplyEmpty}>
        <div className='modal-text'>Reply cannot be empty</div>
      </ReviewReplyAlertModal>
      <ReviewReplyConfirmModal open={showConfirmModalOnDelete} setOpen={setShowConfirmModalOnDelete} confirmationCallback={confirmedDelete}>
        <div className='modal-text'>Are you sure you want to delete your review?</div>
      </ReviewReplyConfirmModal>
    </>
  )
}

ReplyFormSelector.propTypes = {
  reply: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    updatedAt: PropTypes.instanceOf(Date)
  }),
  reviewId: PropTypes.number.isRequired,
  initialForm: PropTypes.string.isRequired
}
